/**
 * Settings > Checklists (approved mockup 2026-10-26): each company's checklist templates. A step
 * says who does it, can tick itself from an automatic check, and can link to a MyDocUploads form
 * (Bill: forms come from MyDocUploads). Changes apply to checklists started from now on.
 */
import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { api, type ChTemplate, type ChTemplateStep } from "../../lib/api";
import { useStore } from "../../lib/store";
import { coName } from "../../lib/brand";
import { Loading, Tile } from "../../components/bits";
import { PartTag } from "./SettingsLayout";

const WHEN: Record<string, string> = { client_onboarding: "starts when a client is added as Onboarding", client_offboarding: "starts when a client is set to Former client",
  agreement_renewal: "starts 90 days before an agreement's notice date", custom: "started by hand" };
const WHO: [string, string][] = [["owner", "Account owner"], ["accounting", "Accounting"], ["admin", "Admin"], ["anyone", "Anyone who edits"]];

export function ChecklistTemplatesPage() {
  const { p, toast } = useStore();
  const codes = p.chCodes.filter((c) => p.ch(c).sensitive);
  const [sp, setSp] = useSearchParams();
  const co = codes.includes(sp.get("co") ?? "") ? sp.get("co")! : codes[0];
  const [data, setData] = useState<Awaited<ReturnType<typeof api.chTemplates>> | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [draft, setDraft] = useState<ChTemplateStep[]>([]);
  const [busy, setBusy] = useState(false);
  const load = async () => { if (co) { const r = await api.chTemplates(co); setData(r); return r; } return null; };
  useEffect(() => { setData(null); setOpen(null); load().then((r) => { const first = r?.templates.find((t) => t.active); if (first) { setOpen(first.id); setDraft(first.steps); } }).catch((e: Error) => toast(e.message)); }, [co]);
  if (!co) return <Navigate to="/settings" replace />;
  const edit = (t: ChTemplate) => { setOpen(t.id); setDraft(t.steps.map((s) => ({ ...s }))); };
  const set = (i: number, patch: Partial<ChTemplateStep>) => setDraft((d) => d.map((s, n) => (n === i ? { ...s, ...patch } : s)));
  const move = (i: number, by: number) => setDraft((d) => { const x = [...d]; const [s] = x.splice(i, 1); x.splice(Math.max(0, Math.min(x.length, i + by)), 0, s!); return x; });
  const save = async (t: ChTemplate) => {
    setBusy(true);
    try { await api.chUpdateTemplate(t.id, { steps: draft.filter((s) => s.title.trim()) }); toast("Saved. New checklists use these steps."); await load(); }
    catch (e) { toast((e as Error).message); } finally { setBusy(false); }
  };
  const rules = data?.auto_rules ?? {};

  return (
    <div className="view">
      <div className="hgroup"><div>
        <PartTag part="clienthub" />
        <h1 className="title">Checklists</h1>
        <div className="lede">The steps for guided work on a client. Changes apply to checklists started from now on.</div>
      </div></div>
      {codes.length > 1 && (
        <div className="ctabs" role="tablist">
          {codes.map((c) => <button key={c} className={`ctab ${c === co ? "on" : ""}`} role="tab" aria-selected={c === co} onClick={() => setSp({ co: c }, { replace: true })}><Tile code={c} /><span>{coName(c)}</span></button>)}
        </div>
      )}
      {!data ? <Loading /> : data.templates.filter((t) => t.active).map((t) => (
        <div key={t.id} className="card sect" style={{ marginBottom: 14 }}>
          <div className="ck-top">
            <h3 style={{ margin: 0 }}>{t.name} <span className="cap">{t.steps.length} steps · {WHEN[t.kind] ?? ""}</span></h3>
            {open !== t.id && <button className="btn btn-ghost btn-sm" onClick={() => edit(t)}>Edit steps</button>}
          </div>
          {open === t.id && <>
            <ul className="tp-list" style={{ marginTop: 10 }}>
              {draft.map((s, i) => (
                <li key={i} className="tp-row">
                  <span className="row-btns" style={{ margin: 0, flexDirection: "column", gap: 0 }}>
                    <button className="btn btn-ghost btn-sm" style={{ padding: "0 6px" }} aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>▲</button>
                    <button className="btn btn-ghost btn-sm" style={{ padding: "0 6px" }} aria-label="Move down" disabled={i === draft.length - 1} onClick={() => move(i, 1)}>▼</button></span>
                  <input className="inp" value={s.title} maxLength={200} aria-label="Step" onChange={(e) => set(i, { title: e.target.value })} />
                  <select className="inp" style={{ width: 150 }} value={s.who ?? "owner"} aria-label="Who" onChange={(e) => set(i, { who: e.target.value })}>{WHO.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                  <select className="inp" style={{ width: 240 }} value={s.auto ?? ""} aria-label="Automatic check" onChange={(e) => set(i, { auto: e.target.value })}>
                    <option value="">No automatic check</option>{Object.entries(rules).map(([k, l]) => <option key={k} value={k}>Ticks itself: {l.charAt(0).toLowerCase() + l.slice(1)}</option>)}</select>
                  <input className="inp" style={{ width: 260 }} value={s.form_url ?? ""} placeholder="MyDocUploads form link (optional)" aria-label="Form link" onChange={(e) => set(i, { form_url: e.target.value })} />
                  <button className="btn btn-ghost btn-sm" aria-label="Remove step" onClick={() => setDraft((d) => d.filter((_, n) => n !== i))}>Remove</button>
                </li>
              ))}
            </ul>
            <div className="row-btns">
              <button className="btn btn-ghost btn-sm" onClick={() => setDraft((d) => [...d, { title: "", who: "owner", auto: "", form_url: "" }])}>+ Add step</button>
              <button className="btn btn-accent btn-sm" disabled={busy || !draft.some((s) => s.title.trim())} onClick={() => void save(t)}>Save</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setOpen(null)}>Cancel</button>
            </div>
          </>}
        </div>
      ))}
    </div>
  );
}
